import React, { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import Loader from "../../Loader";

const GET_PROMOCODES = gql`
  query GetPromocodesForCampaign {
    getPromocode {
      promoCodeList {
        id
        name
        promocode
        discount
        isPercentage
        maxDiscount
        isActive
      }
    }
  }
`;

const GET_CAMPAIGN_STEPS = gql`
  query GetCampaignSteps($campaignId: ID!) {
    getCampaignSteps(campaignId: $campaignId) {
      status
      message
      steps {
        id
        campaignId
        stepNumber
        delayDays
        delayHours
        templateName
        templateData
        incentiveType
        incentiveConfig
      }
    }
  }
`;

const GET_CAMPAIGN_ANALYTICS = gql`
  query GetCampaignAnalytics($campaignId: ID!) {
    getCampaignAnalytics(campaignId: $campaignId) {
      status
      message
      totalUsersStarted
      totalConverted
      usersWithMultipleJourneys
      totalRevenueGenerated
      totalMessagesSent
      stepStats {
        stepNumber
        stepId
        activeUsers
        completedUsers
        failedUsers
      }
    }
  }
`;

const SYNC_CAMPAIGN_STEPS = gql`
  mutation SyncCampaignSteps($campaignId: ID!, $steps: [CampaignStepInput]!) {
    syncCampaignSteps(campaignId: $campaignId, steps: $steps) {
      status
      message
    }
  }
`;

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
      {title}
    </h3>
    <div className="bg-gray-50 border rounded-lg p-4 space-y-2 text-sm text-gray-700">
      {children}
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500 font-medium">{label}</span>
    <span className="text-gray-800 text-right max-w-xs break-words">
      {value ?? "—"}
    </span>
  </div>
);

const Badge = ({ text, color }) => {
  const colors = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
    purple: "bg-purple-100 text-purple-800",
    orange: "bg-orange-100 text-orange-800",
    gray: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-semibold rounded ${colors[color] || colors.gray}`}
    >
      {text}
    </span>
  );
};

const JsonBlock = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="mb-5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
        {label}
      </h3>
      <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-words">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

const STATUS_COLOR = {
  ACTIVE: "green",
  PAUSED: "yellow",
  DRAFT: "blue",
  ARCHIVED: "gray",
};

// ─── Incentive Config Helpers ───────────────────────────────────────

/** Parse stored incentiveConfig JSON into a structured form state for a given type */
function parseIncentiveToForm(incentiveType, incentiveConfig) {
  const cfg = typeof incentiveConfig === "string" && incentiveConfig.trim()
    ? JSON.parse(incentiveConfig)
    : (incentiveConfig || {});

  if (incentiveType === "WALLET_CREDIT") {
    return { walletAmount: cfg.amount || "", walletValidity: cfg.validityDays || "", walletDesc: cfg.description || "Campaign Reward" };
  }
  // Handles both old PROMOCODE (single object) and PROMOCODES (coupons array) — unified
  if (incentiveType === "PROMOCODE" || incentiveType === "PROMOCODES") {
    // Backward compat: old PROMOCODE stored a single object, not a coupons array
    let coupons;
    if (incentiveType === "PROMOCODE") {
      // Wrap the single promo into a 1-item list
      coupons = [cfg];
    } else {
      coupons = cfg.coupons || [];
    }
    return {
      multiPromos: coupons.map(c => ({
        mode: c.existingPromocodeId ? "EXISTING" : "NEW",
        existingId: c.existingPromocodeId || "",
        name: c.name || "", code: c.promocode || "",
        discount: c.discount || "", isPercent: c.isPercentage ? "true" : "false",
        maxDiscount: c.maxDiscount || "", minOrder: c.minOrder || "",
      })),
    };
  }
  if (incentiveType === "MIXED") {
    const wallet = cfg.wallet || {};
    const coupons = cfg.coupons || [];
    return {
      walletAmount: wallet.amount || "",
      walletValidity: wallet.validityDays || "",
      walletDesc: wallet.description || "Campaign Reward",
      multiPromos: coupons.map(c => ({
        mode: c.existingPromocodeId ? "EXISTING" : "NEW",
        existingId: c.existingPromocodeId || "",
        name: c.name || "", code: c.promocode || "",
        discount: c.discount || "", isPercent: c.isPercentage ? "true" : "false",
        maxDiscount: c.maxDiscount || "",
      })),
    };
  }
  // Tiered: each tier picks its own incentive type/config based on the
  // triggering event's cartValue (e.g. cancelled order value) at send time.
  // See CampaignAutomator.resolveStepIncentive in the backend.
  if (incentiveType === "TIERED") {
    const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
    return {
      tiers: tiers.map((t) => ({
        minValue: t.minValue != null ? String(t.minValue) : "",
        maxValue: t.maxValue != null ? String(t.maxValue) : "",
        incentiveType: t.incentiveType || "WALLET_CREDIT",
        offerText: t.templateData?.offer || "",
        templateName: t.templateName || "",
        form: parseIncentiveToForm(t.incentiveType || "WALLET_CREDIT", t.incentiveConfig),
      })),
    };
  }
  return {};
}

/** Serialize structured incentive form state back to incentiveConfig JSON */
function buildIncentiveConfig(incentiveType, form) {
  if (incentiveType === "WALLET_CREDIT") {
    const cfg = { amount: parseFloat(form.walletAmount), category: "PROMOTIONAL", description: form.walletDesc || "Campaign Reward" };
    if (form.walletValidity) cfg.validityDays = parseInt(form.walletValidity);
    return cfg;
  }
  // PROMOCODES handles all promo cases (1 or many). Old PROMOCODE steps also routed here.
  if (incentiveType === "PROMOCODE" || incentiveType === "PROMOCODES") {
    const coupons = (form.multiPromos || []).map(p => {
      if (p.mode === "EXISTING") return { existingPromocodeId: p.existingId };
      return {
        name: p.name, promocode: p.code,
        discount: parseInt(p.discount) || 0,
        isPercentage: p.isPercent === "true",
        maxDiscount: p.maxDiscount ? parseInt(p.maxDiscount) : undefined,
        minOrder: p.minOrder ? parseInt(p.minOrder) : undefined,
        isActive: true,
      };
    });
    return { coupons };
  }
  if (incentiveType === "MIXED") {
    const wallet = { amount: parseFloat(form.walletAmount), category: "PROMOTIONAL", description: form.walletDesc || "Campaign Reward" };
    if (form.walletValidity) wallet.validityDays = parseInt(form.walletValidity);
    const coupons = (form.multiPromos || []).map(p => {
      if (p.mode === "EXISTING") return { existingPromocodeId: p.existingId };
      return { name: p.name, promocode: p.code, discount: parseInt(p.discount) || 0, isPercentage: p.isPercent === "true", maxDiscount: p.maxDiscount ? parseInt(p.maxDiscount) : undefined, isActive: true };
    });
    return { wallet, coupons };
  }
  if (incentiveType === "TIERED") {
    const tiers = (form.tiers || []).map((t) => {
      const tier = {
        incentiveType: t.incentiveType,
        incentiveConfig: buildIncentiveConfig(t.incentiveType, t.form || {}),
        templateData: { offer: t.offerText || "" },
      };
      if (t.minValue !== "" && t.minValue != null) tier.minValue = parseFloat(t.minValue);
      if (t.maxValue !== "" && t.maxValue != null) tier.maxValue = parseFloat(t.maxValue);
      if (t.templateName) tier.templateName = t.templateName;
      return tier;
    });
    return { tiers };
  }
  return null;
}

// ─── Sub-component: structured incentive editor ──────────────────────
const IncentiveEditor = ({ incentiveType, form, onChange, promocodes = [] }) => {
  const fi = (field, value) => onChange({ ...form, [field]: value });

  if (incentiveType === "WALLET_CREDIT") return (
    <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
      <p className="text-xs font-semibold text-amber-800 uppercase">💳 Wallet Credit Configuration</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) <span className="text-red-500">*</span></label>
          <input type="number" min="1" value={form.walletAmount || ""} onChange={e => fi("walletAmount", e.target.value)}
            className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="e.g. 100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Validity (days)</label>
          <input type="number" min="1" value={form.walletValidity || ""} onChange={e => fi("walletValidity", e.target.value)}
            className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="e.g. 7 (blank = no expiry)" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Description (shown in wallet)</label>
          <input type="text" value={form.walletDesc || ""} onChange={e => fi("walletDesc", e.target.value)}
            className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="Campaign Reward" />
        </div>
      </div>
    </div>
  );

  if (incentiveType === "PROMOCODE") return (
    <div className="space-y-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
      <p className="text-xs font-semibold text-purple-800 uppercase">🎟️ Promo Code Configuration</p>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
        <div className="flex gap-3">
          {["EXISTING", "NEW"].map(mode => (
            <label key={mode} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="promoMode" value={mode} checked={form.promoMode === mode} onChange={() => fi("promoMode", mode)} />
              {mode === "EXISTING" ? "Pick existing promo" : "Create new promo"}
            </label>
          ))}
        </div>
      </div>
      {form.promoMode === "EXISTING" ? (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Select Promo Code <span className="text-red-500">*</span></label>
          <select value={form.promoExistingId || ""} onChange={e => fi("promoExistingId", e.target.value)}
            className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500 bg-white">
            <option value="">— Select —</option>
            {promocodes.filter(p => p.isActive).map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.promocode}) — {p.discount}{p.isPercentage ? "%" : "₹"} off</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Promo Name</label>
            <input type="text" value={form.promoName || ""} onChange={e => fi("promoName", e.target.value)}
              className="w-full border p-2 text-sm rounded" placeholder="Summer20" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Promo Code</label>
            <input type="text" value={form.promoCode || ""} onChange={e => fi("promoCode", e.target.value)}
              className="w-full border p-2 text-sm rounded" placeholder="SUMMER20" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Discount Type</label>
            <select value={form.promoIsPercent || "true"} onChange={e => fi("promoIsPercent", e.target.value)}
              className="w-full border p-2 text-sm rounded bg-white">
              <option value="true">Percentage (%)</option>
              <option value="false">Fixed (₹)</option>
            </select></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Discount Value <span className="text-red-500">*</span></label>
            <input type="number" min="1" value={form.promoDiscount || ""} onChange={e => fi("promoDiscount", e.target.value)}
              className="w-full border p-2 text-sm rounded" placeholder="20" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Max Discount (₹)</label>
            <input type="number" min="0" value={form.promoMaxDiscount || ""} onChange={e => fi("promoMaxDiscount", e.target.value)}
              className="w-full border p-2 text-sm rounded" placeholder="200" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Min Order (₹)</label>
            <input type="number" min="0" value={form.promoMinOrder || ""} onChange={e => fi("promoMinOrder", e.target.value)}
              className="w-full border p-2 text-sm rounded" placeholder="0" /></div>
        </div>
      )}
    </div>
  );

  if (incentiveType === "PROMOCODES") {
    const promos = form.multiPromos || [];
    const update = (newList) => fi("multiPromos", newList);
    const addPromo = () => update([...promos, { mode: "EXISTING", existingId: "", name: "", code: "", discount: "", isPercent: "true", maxDiscount: "" }]);
    const removePromo = (i) => update(promos.filter((_, idx) => idx !== i));
    const updatePromo = (i, field, val) => { const copy = [...promos]; copy[i] = { ...copy[i], [field]: val }; update(copy); };

    return (
      <div className="space-y-3 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <p className="text-xs font-semibold text-indigo-800 uppercase">🎟️🎟️ Multiple Promo Codes</p>
          <button type="button" onClick={addPromo} className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700">+ Add Promo</button>
        </div>
        {promos.length === 0 && <p className="text-xs text-gray-400">No promos added yet. Click + Add Promo.</p>}
        {promos.map((p, i) => (
          <div key={i} className="border bg-white rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-xs font-medium text-gray-700">Promo #{i + 1}</p>
              <button type="button" onClick={() => removePromo(i)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
            </div>
            <div className="flex gap-3">
              {["EXISTING", "NEW"].map(m => (
                <label key={m} className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="radio" checked={p.mode === m} onChange={() => updatePromo(i, "mode", m)} />
                  {m === "EXISTING" ? "Existing promo" : "New promo"}
                </label>
              ))}
            </div>
            {p.mode === "EXISTING" ? (
              <select value={p.existingId} onChange={e => updatePromo(i, "existingId", e.target.value)}
                className="w-full border p-1.5 text-xs rounded bg-white">
                <option value="">— Select promo —</option>
                {promocodes.filter(pc => pc.isActive).map(pc => (
                  <option key={pc.id} value={pc.id}>{pc.name} ({pc.promocode}) — {pc.discount}{pc.isPercentage ? "%" : "₹"} off</option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <input className="border p-1.5 text-xs rounded" placeholder="Name" value={p.name} onChange={e => updatePromo(i, "name", e.target.value)} />
                <input className="border p-1.5 text-xs rounded" placeholder="Code" value={p.code} onChange={e => updatePromo(i, "code", e.target.value)} />
                <input className="border p-1.5 text-xs rounded" placeholder="Discount" type="number" value={p.discount} onChange={e => updatePromo(i, "discount", e.target.value)} />
                <select className="border p-1.5 text-xs rounded bg-white" value={p.isPercent} onChange={e => updatePromo(i, "isPercent", e.target.value)}>
                  <option value="true">% off</option>
                  <option value="false">₹ off</option>
                </select>
                <input className="border p-1.5 text-xs rounded" placeholder="Max disc (₹)" type="number" value={p.maxDiscount} onChange={e => updatePromo(i, "maxDiscount", e.target.value)} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (incentiveType === "MIXED") {
    const promos = form.multiPromos || [];
    const update = (newList) => fi("multiPromos", newList);
    const addPromo = () => update([...promos, { mode: "EXISTING", existingId: "", name: "", code: "", discount: "", isPercent: "true", maxDiscount: "" }]);
    const removePromo = (i) => update(promos.filter((_, idx) => idx !== i));
    const updatePromo = (i, field, val) => { const copy = [...promos]; copy[i] = { ...copy[i], [field]: val }; update(copy); };
    return (
      <div className="space-y-4">
        {/* Wallet section */}
        <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-amber-800 uppercase">💳 Wallet Credit (part of Mixed)</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) <span className="text-red-500">*</span></label>
              <input type="number" min="1" value={form.walletAmount || ""} onChange={e => fi("walletAmount", e.target.value)}
                className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="e.g. 50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Validity (days)</label>
              <input type="number" min="1" value={form.walletValidity || ""} onChange={e => fi("walletValidity", e.target.value)}
                className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="e.g. 7" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input type="text" value={form.walletDesc || ""} onChange={e => fi("walletDesc", e.target.value)}
                className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500" placeholder="Campaign Reward" />
            </div>
          </div>
        </div>
        {/* Promos section */}
        <div className="space-y-3 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <p className="text-xs font-semibold text-indigo-800 uppercase">🎟️ Promo Codes (part of Mixed)</p>
            <button type="button" onClick={addPromo} className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700">+ Add Promo</button>
          </div>
          {promos.length === 0 && <p className="text-xs text-gray-400">No promos added yet — wallet credit will still be applied.</p>}
          {promos.map((p, i) => (
            <div key={i} className="border bg-white rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center">
                <p className="text-xs font-medium text-gray-700">Promo #{i + 1}</p>
                <button type="button" onClick={() => removePromo(i)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
              <div className="flex gap-3">
                {["EXISTING", "NEW"].map(m => (
                  <label key={m} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" checked={p.mode === m} onChange={() => updatePromo(i, "mode", m)} />
                    {m === "EXISTING" ? "Existing promo" : "New promo"}
                  </label>
                ))}
              </div>
              {p.mode === "EXISTING" ? (
                <select value={p.existingId} onChange={e => updatePromo(i, "existingId", e.target.value)}
                  className="w-full border p-1.5 text-xs rounded bg-white">
                  <option value="">— Select promo —</option>
                  {promocodes.filter(pc => pc.isActive).map(pc => (
                    <option key={pc.id} value={pc.id}>{pc.name} ({pc.promocode}) — {pc.discount}{pc.isPercentage ? "%" : "₹"} off</option>
                  ))}
                </select>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <input className="border p-1.5 text-xs rounded" placeholder="Name" value={p.name} onChange={e => updatePromo(i, "name", e.target.value)} />
                  <input className="border p-1.5 text-xs rounded" placeholder="Code" value={p.code} onChange={e => updatePromo(i, "code", e.target.value)} />
                  <input className="border p-1.5 text-xs rounded" placeholder="Discount" type="number" value={p.discount} onChange={e => updatePromo(i, "discount", e.target.value)} />
                  <select className="border p-1.5 text-xs rounded bg-white" value={p.isPercent} onChange={e => updatePromo(i, "isPercent", e.target.value)}>
                    <option value="true">% off</option>
                    <option value="false">₹ off</option>
                  </select>
                  <input className="border p-1.5 text-xs rounded" placeholder="Max disc (₹)" type="number" value={p.maxDiscount} onChange={e => updatePromo(i, "maxDiscount", e.target.value)} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (incentiveType === "TIERED") {
    const tiers = form.tiers || [];
    const update = (newList) => fi("tiers", newList);
    const addTier = () => update([...tiers, {
      minValue: "", maxValue: "", incentiveType: "WALLET_CREDIT", offerText: "", templateName: "",
      form: { walletAmount: "", walletValidity: "", walletDesc: "Campaign Reward" },
    }]);
    const removeTier = (i) => update(tiers.filter((_, idx) => idx !== i));
    const updateTier = (i, field, val) => {
      const copy = [...tiers];
      copy[i] = { ...copy[i], [field]: val };
      update(copy);
    };
    const updateTierType = (i, newType) => {
      const copy = [...tiers];
      copy[i] = {
        ...copy[i],
        incentiveType: newType,
        form: newType === "PROMOCODES" ? { multiPromos: [] }
            : newType === "WALLET_CREDIT" ? { walletAmount: "", walletValidity: "", walletDesc: "Campaign Reward" }
            : newType === "MIXED" ? { walletAmount: "", walletValidity: "", walletDesc: "Campaign Reward", multiPromos: [] }
            : {},
      };
      update(copy);
    };

    return (
      <div className="space-y-3 bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <p className="text-xs font-semibold text-teal-800 uppercase">🎯 Tiered by Cart Value</p>
          <button type="button" onClick={addTier} className="text-xs bg-teal-600 text-white px-3 py-1 rounded hover:bg-teal-700">+ Add Tier</button>
        </div>
        <p className="text-[11px] text-gray-500">
          Ranges are inclusive and matched against the cart value of the order that triggered this journey (e.g. the cancelled booking's price). Leave Min/Max blank for an open-ended tier. If no tier matches, the message still sends with no incentive.
        </p>
        {tiers.length === 0 && <p className="text-xs text-gray-400">No tiers yet. Click + Add Tier.</p>}
        {tiers.map((t, i) => (
          <div key={i} className="border bg-white rounded-lg p-3 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs font-medium text-gray-700">Tier #{i + 1}</p>
              <button type="button" onClick={() => removeTier(i)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Cart Value (₹)</label>
                <input type="number" min="0" value={t.minValue} onChange={e => updateTier(i, "minValue", e.target.value)}
                  className="w-full border p-2 text-sm rounded" placeholder="e.g. 800 (blank = no floor)" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Cart Value (₹)</label>
                <input type="number" min="0" value={t.maxValue} onChange={e => updateTier(i, "maxValue", e.target.value)}
                  className="w-full border p-2 text-sm rounded" placeholder="e.g. 1999 (blank = no ceiling)" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Offer text shown in the message <span className="text-red-500">*</span>
              </label>
              <input type="text" value={t.offerText} onChange={e => updateTier(i, "offerText", e.target.value)}
                className="w-full border p-2 text-sm rounded" placeholder="e.g. ₹50 wallet credit, or 15% off your next booking" />
              <p className="text-[11px] text-gray-400 mt-1">Fills the template's offer variable — write it as a self-contained phrase so it reads naturally regardless of incentive type.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Override (optional)</label>
              <input type="text" value={t.templateName} onChange={e => updateTier(i, "templateName", e.target.value)}
                className="w-full border p-2 text-sm rounded" placeholder="Leave blank to use this step's template" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Incentive Type for this tier</label>
              <select value={t.incentiveType} onChange={e => updateTierType(i, e.target.value)}
                className="w-full border p-2 text-sm rounded bg-white">
                <option value="WALLET_CREDIT">💳 Wallet Credit</option>
                <option value="PROMOCODES">🎟️ Promo Code(s)</option>
                <option value="MIXED">💳🎟️ Mixed (Wallet + Promos)</option>
              </select>
            </div>
            <IncentiveEditor
              incentiveType={t.incentiveType}
              form={t.form || {}}
              onChange={(newForm) => updateTier(i, "form", newForm)}
              promocodes={promocodes}
            />
          </div>
        ))}
      </div>
    );
  }

  return null;
};

// ─── Main Drawer Component ─────────────────────────────────────────
const CampaignDetailDrawer = ({ campaign, onClose, onExecute, executing, onHalt, onResume }) => {
  const [activeTab, setActiveTab] = useState("OVERVIEW");
  const [steps, setSteps] = useState([]);
  const [editingStepIndex, setEditingStepIndex] = useState(null);

  // Incentive form state per step index: { [idx]: { walletAmount, ... } }
  const [incentiveForms, setIncentiveForms] = useState({});

  const { data: promoData } = useQuery(GET_PROMOCODES, { fetchPolicy: "network-only" });
  const promocodes = promoData?.getPromocode?.promoCodeList || [];

  const { data, loading, refetch } = useQuery(GET_CAMPAIGN_STEPS, {
    variables: { campaignId: campaign?.id },
    skip: !campaign?.id,
    fetchPolicy: "network-only",
  });

  const { data: analyticsData, loading: loadingAnalytics } = useQuery(GET_CAMPAIGN_ANALYTICS, {
    variables: { campaignId: campaign?.id },
    skip: !campaign?.id,
    fetchPolicy: "network-only",
  });

  const [syncStepsMutation, { loading: syncing }] =
    useMutation(SYNC_CAMPAIGN_STEPS);

  useEffect(() => {
    if (data?.getCampaignSteps?.steps) {
      const loaded = data.getCampaignSteps.steps.map((s) => ({
        id: s.id,
        stepNumber: s.stepNumber,
        delayDays: s.delayDays,
        delayHours: s.delayHours || 0,
        templateName: s.templateName || "",
        templateData: s.templateData ? JSON.stringify(s.templateData, null, 2) : "",
        incentiveType: s.incentiveType || "",
      }));
      setSteps(loaded);
      // Hydrate incentive forms from existing config
      const forms = {};
      data.getCampaignSteps.steps.forEach((s, idx) => {
        if (s.incentiveType) {
          forms[idx] = parseIncentiveToForm(s.incentiveType, s.incentiveConfig);
        }
      });
      setIncentiveForms(forms);
    }
  }, [data]);

  if (!campaign) return null;

  const handleAddStep = () => {
    setSteps([
      ...steps,
      { stepNumber: steps.length + 1, delayDays: 1, delayHours: 0, templateName: "", templateData: "", incentiveType: "" },
    ]);
    setEditingStepIndex(steps.length);
  };

  /** e.g. "2h", "1d", "1d 2h" */
  const formatDelay = (delayDays, delayHours) => {
    const d = parseInt(delayDays) || 0;
    const h = parseInt(delayHours) || 0;
    if (d === 0 && h === 0) return "Immediately";
    return [d > 0 ? `${d}d` : null, h > 0 ? `${h}h` : null].filter(Boolean).join(" ");
  };

  const handleIncentiveTypeChange = (idx, newType) => {
    handleStepChange(idx, "incentiveType", newType);
    // Reset the incentive form for this step
    setIncentiveForms(prev => ({
      ...prev,
      [idx]: newType === "PROMOCODES" ? { multiPromos: [] }
           : newType === "WALLET_CREDIT" ? { walletAmount: "", walletValidity: "", walletDesc: "Campaign Reward" }
           : newType === "MIXED" ? { walletAmount: "", walletValidity: "", walletDesc: "Campaign Reward", multiPromos: [] }
           : newType === "TIERED" ? { tiers: [] }
           : {},
    }));
  };

  const handleRemoveStep = (index) => {
    if (window.confirm("Remove this step?")) {
      const newSteps = steps
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, stepNumber: i + 1 }));
      setSteps(newSteps);
      setEditingStepIndex(null);
    }
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleSaveSteps = async () => {
    try {
      const payload = steps.map((s, idx) => {
        let parsedTemplateData = null;
        if (s.templateData && s.templateData.trim() !== "") {
          parsedTemplateData = JSON.parse(s.templateData);
        }
        // Build incentive config from structured form, not raw JSON
        const incentiveConfig = s.incentiveType
          ? buildIncentiveConfig(s.incentiveType, incentiveForms[idx] || {})
          : null;
        return {
          id: s.id,
          stepNumber: parseInt(s.stepNumber),
          delayDays: parseInt(s.delayDays) || 0,
          delayHours: parseInt(s.delayHours) || 0,
          templateName: s.templateName || null,
          templateData: parsedTemplateData,
          incentiveType: s.incentiveType || null,
          incentiveConfig,
        };
      });

      const res = await syncStepsMutation({
        variables: { campaignId: campaign.id, steps: payload },
      });

      if (res.data?.syncCampaignSteps?.status === "SUCCESS") {
        alert("Steps saved successfully!");
        refetch();
        setEditingStepIndex(null);
      } else {
        alert("Failed to save steps: " + res.data?.syncCampaignSteps?.message);
      }
    } catch (err) {
      alert("Error saving steps: " + err.message);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-30"
        onClick={onClose}
      />

      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start px-6 py-5 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {campaign.name}
            </h2>
            <div className="flex gap-2 mt-2">
              <Badge
                text={campaign.status}
                color={STATUS_COLOR[campaign.status] || "gray"}
              />
              {campaign.triggerType && (
                <Badge text={campaign.triggerType} color="purple" />
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6 pt-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab("OVERVIEW")}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === "OVERVIEW"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("ANALYTICS")}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === "ANALYTICS"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Analytics & Performance
          </button>
          <button
            onClick={() => setActiveTab("JOURNEY")}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === "JOURNEY"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Journey Builder (Steps)
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {/* TAB 1: OVERVIEW */}
          {activeTab === "OVERVIEW" && (
            <div className="bg-white p-5 rounded-lg border shadow-sm space-y-5">
              <Section title="Campaign Identity">
                <Row label="Type" value={campaign.campaignType} />
                <Row label="Trigger" value={campaign.triggerType} />
                <Row label="Channel" value={campaign.communicationChannel} />
                <Row
                  label="WhatsApp Template"
                  value={campaign.whatsappTemplate}
                />
              </Section>
              <Section title="Timing & Attribution">
                <Row label="Priority" value={campaign.priority} />
                <Row
                  label="Cooldown"
                  value={
                    campaign.cooldownHours
                      ? `${campaign.cooldownHours} hours`
                      : "—"
                  }
                />
                <Row
                  label="Attribution Window"
                  value={
                    campaign.attributionWindowHours
                      ? `${campaign.attributionWindowHours} hours`
                      : "—"
                  }
                />
                <Row
                  label="Batch Release"
                  value={
                    campaign.batchSize
                      ? `${campaign.batchSize} users every ${campaign.batchIntervalHours || "?"} hours`
                      : "Off — activates whole audience immediately"
                  }
                />
              </Section>
              <JsonBlock
                label="Audience Rules"
                value={campaign.audienceRules}
              />
              <JsonBlock
                label="Incentive Config"
                value={campaign.incentiveConfig}
              />
              <JsonBlock
                label="Metadata / Schedule"
                value={campaign.metadata}
              />
            </div>
          )}

          {/* TAB: ANALYTICS */}
          {activeTab === "ANALYTICS" && (
            <div className="animate-fade-in space-y-6">
              {loadingAnalytics ? (
                <div className="py-10 text-center"><Loader /></div>
              ) : analyticsData?.getCampaignAnalytics ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <h4 className="text-sm font-medium text-blue-800">Total Users Started</h4>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {analyticsData.getCampaignAnalytics.totalUsersStarted}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                      <h4 className="text-sm font-medium text-green-800">Total Converted</h4>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {analyticsData.getCampaignAnalytics.totalConverted}
                      </p>
                      <p className="text-xs text-green-700 mt-1 opacity-80">Booked after journey start</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                      <h4 className="text-sm font-medium text-purple-800">Total Revenue</h4>
                      <p className="text-2xl font-bold text-purple-900 mt-1">
                        ₹{analyticsData.getCampaignAnalytics.totalRevenueGenerated?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                      <h4 className="text-sm font-medium text-orange-800">Repeat Journeys</h4>
                      <p className="text-2xl font-bold text-orange-900 mt-1">
                        {analyticsData.getCampaignAnalytics.usersWithMultipleJourneys}
                      </p>
                      <p className="text-xs text-orange-700 mt-1 opacity-80">Users placed in this campaign multiple times</p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-sm font-semibold text-gray-800 mb-4">Step-by-Step Distribution</h3>
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="py-3 px-4 font-medium text-gray-600">Step</th>
                            <th className="py-3 px-4 font-medium text-gray-600">Waiting (Active)</th>
                            <th className="py-3 px-4 font-medium text-gray-600">Sent (Completed)</th>
                            <th className="py-3 px-4 font-medium text-gray-600">Failed</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {analyticsData.getCampaignAnalytics.stepStats?.map((s) => (
                            <tr key={s.stepId} className="hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium text-gray-800">Step {s.stepNumber}</td>
                              <td className="py-3 px-4 text-blue-600 font-semibold">{s.activeUsers}</td>
                              <td className="py-3 px-4 text-green-600 font-semibold">{s.completedUsers}</td>
                              <td className="py-3 px-4 text-red-600 font-semibold">{s.failedUsers}</td>
                            </tr>
                          ))}
                          {(!analyticsData.getCampaignAnalytics.stepStats || analyticsData.getCampaignAnalytics.stepStats.length === 0) && (
                            <tr>
                              <td colSpan="4" className="py-6 text-center text-gray-500">No step data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-10 text-center text-gray-500">Failed to load analytics</div>
              )}
            </div>
          )}

          {/* TAB 2: JOURNEY BUILDER */}
          {activeTab === "JOURNEY" && (
            <div className="space-y-4">
              {loading ? (
                <Loader />
              ) : (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Automation Sequence
                    </h3>
                    <button
                      onClick={handleAddStep}
                      className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-200 transition"
                    >
                      + Add Step
                    </button>
                  </div>

                  {steps.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 bg-white rounded-lg border border-dashed">
                      No steps configured. This campaign will only fire once.
                    </div>
                  ) : (
                    <div className="space-y-4 relative">
                      {/* Vertical line connecting steps */}
                      <div className="absolute left-[23px] top-4 bottom-4 w-0.5 bg-gray-300 z-0" />

                      {steps.map((step, idx) => (
                        <div key={idx} className="relative z-10 flex gap-4">
                          {/* Circle indicator */}
                          <div className="w-12 h-12 rounded-full bg-white border-2 border-blue-500 flex items-center justify-center font-bold text-blue-600 shadow-sm shrink-0">
                            {idx + 1}
                          </div>

                          {/* Step Card */}
                          <div className="flex-1 bg-white rounded-lg border shadow-sm overflow-hidden transition-all">
                            {/* Card Header */}
                            <div
                              className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center cursor-pointer hover:bg-gray-100"
                              onClick={() =>
                                setEditingStepIndex(
                                  editingStepIndex === idx ? null : idx,
                                )
                              }
                            >
                              <div>
                                <span className="font-semibold text-gray-800">
                                  {formatDelay(step.delayDays, step.delayHours)}
                                </span>
                                <span className="text-gray-500 text-sm ml-2">
                                  {step.templateName
                                    ? `Send: ${step.templateName}`
                                    : "Wait"}
                                  {step.incentiveType
                                    ? ` • Assign ${step.incentiveType}`
                                    : ""}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveStep(idx);
                                }}
                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                              >
                                Remove
                              </button>
                            </div>

                            {/* Edit Form (Collapsible) */}
                            {editingStepIndex === idx && (
                              <div className="p-4 space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Delay — Days
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={step.delayDays}
                                      onChange={(e) =>
                                        handleStepChange(
                                          idx,
                                          "delayDays",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Delay — Hours
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="23"
                                      value={step.delayHours || 0}
                                      onChange={(e) =>
                                        handleStepChange(
                                          idx,
                                          "delayHours",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">
                                      From previous step (or enrollment, for step 1)
                                    </p>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Template Name (WhatsApp)
                                    </label>
                                    <input
                                      type="text"
                                      value={step.templateName}
                                      onChange={(e) =>
                                        handleStepChange(
                                          idx,
                                          "templateName",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500"
                                      placeholder="e.g. reminder_v1"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Template Data (JSON)
                                  </label>
                                  <textarea
                                    value={step.templateData}
                                    onChange={(e) =>
                                      handleStepChange(
                                        idx,
                                        "templateData",
                                        e.target.value,
                                      )
                                    }
                                    rows="2"
                                    className="w-full border p-2 text-sm font-mono rounded focus:ring-1 focus:ring-blue-500"
                                    placeholder='e.g. { "name": "User", "date": "tomorrow" }'
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Incentive Type
                                  </label>
                                  <select
                                    value={step.incentiveType}
                                    onChange={(e) => handleIncentiveTypeChange(idx, e.target.value)}
                                    className="w-full border p-2 text-sm rounded focus:ring-1 focus:ring-blue-500 bg-white"
                                  >
                                    <option value="">No Incentive</option>
                                    <option value="WALLET_CREDIT">💳 Wallet Credit</option>
                                    <option value="PROMOCODES">🎟️ Promo Code(s)</option>
                                    <option value="MIXED">💳🎟️ Mixed (Wallet + Promos)</option>
                                    <option value="TIERED">🎯 Tiered by Cart Value</option>
                                  </select>
                                </div>
                                {step.incentiveType && (
                                  <IncentiveEditor
                                    incentiveType={step.incentiveType}
                                    form={incentiveForms[idx] || {}}
                                    onChange={(newForm) =>
                                      setIncentiveForms(prev => ({ ...prev, [idx]: newForm }))
                                    }
                                    promocodes={promocodes}
                                  />
                                )}
                                {/* Live preview of generated JSON */}
                                {step.incentiveType && (incentiveForms[idx]) && (
                                  <details className="mt-1">
                                    <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Preview JSON →</summary>
                                    <pre className="mt-1 text-xs bg-gray-900 text-green-400 rounded p-2 overflow-x-auto">
                                      {JSON.stringify(buildIncentiveConfig(step.incentiveType, incentiveForms[idx] || {}), null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-4 border-t mt-6 flex justify-end">
                    <button
                      onClick={handleSaveSteps}
                      disabled={syncing}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium shadow-sm disabled:opacity-50 transition"
                    >
                      {syncing ? "Saving..." : "Save Journey Sequence"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t bg-gray-50 flex gap-3">
          {campaign.status === "ACTIVE" && (
            <button
              onClick={() => onExecute(campaign)}
              disabled={executing === campaign.id}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {executing === campaign.id
                ? "Executing..."
                : "▶ Execute Campaign Now"}
            </button>
          )}
          {(campaign.status === "ACTIVE" || campaign.status === "PAUSED") && onHalt && (
            <button
              onClick={() => onHalt(campaign)}
              title="Pause/Resume above only controls NEW enrollment — this separately stops messages already scheduled but not yet sent"
              className="flex-1 py-2.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition"
            >
              ⛔ Halt Pending Sends
            </button>
          )}
          {campaign.status === "PAUSED" && onResume && (
            <button
              onClick={() => onResume(campaign)}
              title="Resumes messages previously halted with 'Halt Pending Sends', re-staggered into fresh batches starting now"
              className="flex-1 py-2.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg text-sm font-medium transition"
            >
              ▶ Resume Pending Sends
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default CampaignDetailDrawer;
